import React from 'react';
import { Box, Link, Paper, Typography } from '@mui/material';
import CommonPageBaseLayout from '../../organism/Common.PageBaseLayout/Common.PageBaseLayout';

type InfoSection = {
  id: string;
  title: string;
  content: React.ReactNode;
};

const scrollToSection = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
  event.preventDefault();
  const section = document.getElementById(id);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const sectionList: Array<{ id: string; title: string }> = [
  { id: 'what-is-spoton', title: 'What Is SpotOn?' },
  { id: 'campaign-metrics', title: 'Campaign metrics' },
  { id: 'definitions', title: 'Definitions' },
  { id: 'media-channels', title: 'List of Media / Channels Included' },
  { id: 'data-update-schedule', title: 'Data Update Schedule' },
  { id: 'updates-and-alerts', title: 'Updates and Alerts' },
  { id: 'datasets-used', title: 'Datasets used in SpotOn' },
];

const Paragraph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle1" sx={{ mb: 2, lineHeight: 1.75 }}>
    {children}
  </Typography>
);

const BulletList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
    {items.map((item, index) => (
      <Typography component="li" variant="subtitle1" sx={{ mb: 1, lineHeight: 1.7 }} key={index}>
        {item}
      </Typography>
    ))}
  </Box>
);

const sections: InfoSection[] = [
  {
    id: 'what-is-spoton',
    title: 'WHAT IS SPOTON?',
    content: (
      <>
        <Paragraph>
          SpotOn is a standardized, cross-platform measurement solution for TV, streaming, and online video advertising. It provides unified reach and frequency metrics for campaigns in predefined target groups, using a transparent industry standard with strict contact definitions.
        </Paragraph>
        <BulletList
          items={[
            'A contact for digital (streaming or online) ad is counted only when viewing is user-initiated, and the video player remains 100% visible on screen for the entire impression.',
            'For TV (linear), a contact is defined in the same way as in TV meter data, including viewing in 7 days after broadcast.',
          ]}
        />
        <Box sx={{ mt: 2 }}>
          <Paragraph>
            The methodology of SpotOn is designed to meet the needs of the Finnish market and the development has been led by commercial broadcasters operating in Finland. SpotOn measurement is owned and produced by Media Metrics Finland (MMF). The governance of the measurement is handled by a dedicated steering group. SpotOn measurement is produced by dataBreeders and validated by an independent third party.
          </Paragraph>
        </Box>
      </>
    ),
  },
  {
    id: 'campaign-metrics',
    title: 'CAMPAIGN METRICS',
    content: (
      <>
        <Paragraph>
          SpotOn provides campaign metrics broken down by:
        </Paragraph>
        <BulletList
          items={[
            'Streaming services contacts by company',
            'TV contacts by company',
            'Online video contacts by company',
            'Contacts by device type: TV Screen / Small Screen',
            'Target group: Gender and age',
          ]}
        />
        <Box sx={{ mt: 2 }}>
          <Paragraph>
            Post-evaluations are configured using the following user-defined inputs:
          </Paragraph>
          <BulletList
            items={[
              'Spotgate IDs: Used to identify the advertisements included in the analysis',
              'Targets: Define the audience segments for which KPIs are calculated',
              'Channels: Allow users to limit the analysis to selected channels',
            ]}
          />
        </Box>
      </>
    ),
  },
  {
    id: 'definitions',
    title: 'DEFINITIONS',
    content: (
      <BulletList
        items={[
          'Contacts: The number of individuals for TV, streaming services and online video ads',
          'Online Video: Instream video ads streamed alongside video content viewed on websites (e.g. news media or social media).',
          'Streaming Services: Ads on streaming services, including broadcaster VOD players and other video streaming platforms focused on TV-like content (such as movies and series), and linear channels streamed on devices other than TV sets, including FAST channels (Free Ad-Supported Streaming TV).',
          'TRP (Target Rating Point): The number of contacts in a given target group divided by the size of the target group multiplied by 100.',
          'TV: Ads on linear television viewed on TV sets (live or time-shifted), including channels distributed via terrestrial, cable, IPTV, or satellite platforms.',
          '30sec equivalent: For campaigns that include spot length that are longer or less than 30 seconds the report is recalculated to the equivalent of 30 sec spots.',
        ]}
      />
    ),
  },
  {
    id: 'media-channels',
    title: 'LIST OF MEDIA / CHANNELS INCLUDED',
    content: (
      <>
        <Paragraph>
          Media Included in SpotOn Measurement:
        </Paragraph>
        <BulletList
          items={[
            <>MTV<Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}><Typography component="li" variant="subtitle1" sx={{ mb: 1, lineHeight: 1.7 }}>TV: MTV3, MTV Sub, MTV Ava, STAR Channel, National Geographic</Typography><Typography component="li" variant="subtitle1" sx={{ lineHeight: 1.7 }}>Streaming Services: MTV Katsomo</Typography></Box></>,
            <>Sanoma<Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}><Typography component="li" variant="subtitle1" sx={{ mb: 1, lineHeight: 1.7 }}>TV: Nelonen, Jim, Liv, Hero, Viaplay TV, Viaplay 1 Urheilu, Viaplay 2 Urheilu, Viaplay 3 Urheilu</Typography><Typography component="li" variant="subtitle1" sx={{ mb: 1, lineHeight: 1.7 }}>Streaming Services: Ruutu</Typography><Typography component="li" variant="subtitle1" sx={{ lineHeight: 1.7 }}>Online Video: IS video (news media)</Typography></Box></>,
            <>Warner Bros. Discovery<Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}><Typography component="li" variant="subtitle1" sx={{ lineHeight: 1.7 }}>TV: TV5, Kutonen, Frii, TLC, Discovery Channel, Eurosport1</Typography></Box></>,
          ]}
        />
        <Box sx={{ mt: 2 }}>
          <Paragraph>
            Please note that this is a current list of included media. When running historical data reports, please remember to consider the possible changes in media channels.
          </Paragraph>
        </Box>
      </>
    ),
  },
  {
    id: 'data-update-schedule',
    title: 'DATA UPDATE SCHEDULE',
    content: (
      <>
        <Paragraph>
          SpotOn delivers data daily. Under normal conditions, data is released at 6:00 PM EET on the following day.
        </Paragraph>
        <Paragraph>
          Please note that the standard TV Day runs from 02:00 AM to 02:00 AM EET.
        </Paragraph>
        <Paragraph>
          Please note that time-shifted viewing is measured and updated daily for one week after the campaign ends. Therefore, the final analysis for campaigns including linear TV is available 8 days after the campaign ends.
        </Paragraph>
      </>
    ),
  },
  {
    id: 'updates-and-alerts',
    title: 'UPDATES AND ALERTS',
    content: (
      <>
        <Paragraph>
          The Relevant Status Updates section displays major updates related to the SpotOn measurement system. These can be found under Service Status at the top of the page.
        </Paragraph>
        <Paragraph>
          If a Warning icon appears in your analysis, it means that the included data is currently under review by the system maintenance team. We recommend rerunning the analysis later. The data is considered valid once the Warning icon no longer appears. For more information about data-related issues, please refer to <Link href="mailto:spotonvideo.support@databreeders.com">spotonvideo.support@databreeders.com</Link>.
        </Paragraph>
      </>
    ),
  },
  {
    id: 'datasets-used',
    title: 'DATASETS USED IN SPOTON',
    content: (
      <>
        <Paragraph>
          SpotOn measurement and post-evaluation are based on the following data sources:
        </Paragraph>
        <BulletList
          items={[
            'Finnpanel Panel and TV Spot Data: Finnpanel’s panel uses Focal Meters to measure video viewing and to identify panelists’ ad server events across devices. Combined with TAM spot data, this enables deduplicated reach and frequency (R&F) calculations within predefined target groups.',
            'Companies’ Ad Server Data: Companies provide first-party ad server data detailing how many impressions were delivered, when they were delivered, to which target groups, and on which devices. Only user-initiated impressions that were viewed to completion are included.',
            'Spotgate ID: Spotgate ID enables consistent identification of advertisements across TV spot data and ad server data, ensuring reliable linkage between datasets.',
          ]}
        />
        <Box sx={{ mt: 2 }}>
          <Paragraph>
            Post-evaluations are configured using the following user-defined inputs:
          </Paragraph>
          <BulletList
            items={[
              'Spotgate IDs: Used to identify the advertisements included in the analysis',
              'Targets: Define the audience segments for which KPIs are calculated',
              'Channels: Allow users to limit the analysis to selected channels',
            ]}
          />
        </Box>
      </>
    ),
  },
];

const Info: React.FC = () => {
  return (
    <CommonPageBaseLayout>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Typography variant="h1" fontWeight={600}>
          Info
        </Typography>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, pt: 1 }}>
          Last Updated on 12/05/2026
        </Typography>
      </Box>
      <Typography variant="subtitle1" mt={1} sx={{ lineHeight: 1.75 }}>
        SpotOn measurement’s dashboard is designed for the post-evaluation of individual campaigns. It is optimized to analyze one campaign at a time to ensure accurate and reliable results. Submitting large batches or massive requests involving many campaigns simultaneously may lead to performance issues. For bulk analyses, please run evaluations sequentially or contact the support team.
      </Typography>
      <Typography variant="subtitle1" mt={1} sx={{ lineHeight: 1.75 }}>
        SpotOn data is updated daily and made available the following day at 6:00 pm EET. Please note that the Standard TV Day goes from 02:00 am EET to 02:00 am EET.
      </Typography>

      <Paper sx={{ padding: 3, borderRadius: '15px', mt: 5, mb: 5 }}>
        <Typography variant="h5" fontWeight={"bold"} sx={{ mb: 2 }}>ON THIS PAGE</Typography>
        <Box component="ol" sx={{ m: 0, pl: 3 }}>
          {sectionList.map((section) => (
            <Typography component="li" variant="subtitle1" sx={{ mb: 1.25, lineHeight: 1.7 }} key={section.id}>
              <Link href={`#${section.id}`} underline="hover" onClick={scrollToSection(section.id)} sx={{ cursor: 'pointer' }}>
                {section.title}
              </Link>
            </Typography>
          ))}
        </Box>
      </Paper>

      <Box sx={{ marginBottom: '5%' }}>
        {sections.map((section) => (
          <Box id={section.id} key={section.id} sx={{ scrollMarginTop: '110px' }}>
            <Paper sx={{ padding: 2.5, minHeight: '100%', borderRadius: '15px', mb: '4%' }}>
              <Typography variant="h5" fontWeight={"bold"} sx={{ mb: 2 }}>
                {section.title}
              </Typography>
              {section.content}
            </Paper>
          </Box>
        ))}
      </Box>
    </CommonPageBaseLayout>
  );
};

export default Info;
